import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { getCryoList } from "./api";

export default function CryoQrPrint() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const macFromQs = sp.get("mac") || "";

  const [device, setDevice] = useState(null);
  const [me, setMe] = useState(null);
  const [qrReady, setQrReady] = useState(false);
  const [printerMessage, setPrinterMessage] = useState("");
  const labelPdfUrl = `/api/frighi/qr-label-pdf.php?${new URLSearchParams({ id }).toString()}`;

  useEffect(() => {
    const onPrintResult = (event) => {
      setPrinterMessage(event.detail?.success
        ? "Etichetta inviata alla stampante."
        : event.detail?.message || "Stampa non riuscita. Controlla la stampante e riprova.");
    };
    window.addEventListener("norma-printer-print-result", onPrintResult);
    return () => window.removeEventListener("norma-printer-print-result", onPrintResult);
  }, []);

  const printThermalLabel = (event) => {
    // The installed Android app already exposes this bridge. Call it directly:
    // opening the PDF instead would invoke the generic document actions.
    const printer = typeof window.AndroidPrinter?.printPdfLabel === "function"
      ? window.AndroidPrinter
      : window.NormaPrinter;
    if (typeof printer?.printPdfLabel !== "function") return;
    event.preventDefault();
    try {
      const status = typeof printer.getPrinterStatus === "function"
        ? JSON.parse(printer.getPrinterStatus())
        : typeof printer.getStatus === "function" ? printer.getStatus() : null;
      if (status && !status.connected) {
        setPrinterMessage("Collega l'etichettatrice, poi premi di nuovo Stampa con etichettatrice.");
        if (typeof printer.selectBluetoothPrinter === "function") printer.selectBluetoothPrinter();
        else if (typeof printer.connect === "function") printer.connect();
        return;
      }
      setPrinterMessage("Invio etichetta alla stampante…");
      printer.printPdfLabel(new URL(labelPdfUrl, window.location.origin).href, 80, 50, 2, 1);
    } catch {
      setPrinterMessage("Impossibile avviare la stampa. Controlla il collegamento della stampante e riprova.");
    }
  };

  // URL pagina inserimento manuale
  const url = useMemo(() => {
    const origin = window.location.origin;
    const qs = macFromQs ? `?mac=${encodeURIComponent(macFromQs)}` : "";
    return `${origin}/frighi/manual/${id}${qs}`;
  }, [id, macFromQs]);

  // QR esterno (PNG)
  const qrUrl = useMemo(() => {
    return (
      "https://api.qrserver.com/v1/create-qr-code/?" +
      new URLSearchParams({
        size: "300x300",
        data: url,
      }).toString()
    );
  }, [url]);

  useEffect(() => {
    let stop = false;

    (async () => {
      try {
        // carico device
        const rows = await getCryoList({ limit: 400 });
        if (stop) return;
        const found = rows.find((r) => String(r.id) === String(id));
        setDevice(found || null);
      } catch {
        // ok: stampo comunque
      }
    })();

    (async () => {
      try {
        const r = await fetch("/api/me.php", { credentials: "include" });
        const data = await r.json();
        if (!stop) setMe(data || null);
      } catch {
        // ok: azienda opzionale
      }
    })();

    return () => {
      stop = true;
    };
  }, [id]);

  const mac =
    device?.mac ?? device?.mac_address ?? device?.macAddress ?? macFromQs ?? "";

  const frigoName =
    device?.nome ||
    device?.name ||
    device?.label ||
    (mac ? `Frigo ${mac}` : `Frigo ${id}`);

  const reparto =
    device?.reparto || device?.zona || device?.location || "";

  // prova a prendere un nome azienda “buono”
  const azienda =
    me?.azienda_nome ||
    me?.azienda ||
    me?.company ||
    me?.nome_azienda ||
    "";

  const logoSrc = "/cryo-logo.png"; // oppure "/logo192.png"

  return (
    <div className="qr-print-page min-h-screen bg-slate-50 p-4 sm:p-8 print:p-0">
      <style>{`
        @page { size: A6 portrait; margin: 0; }
        .qr-sticker { width: 105mm; min-height: 148mm; }
        .print-hide { display: block; }
        @media print {
          html, body, #root {
            width: 105mm !important;
            height: 148mm !important;
            min-width: 105mm !important;
            min-height: 148mm !important;
            margin: 0 !important;
            padding: 0 !important;
            overflow: hidden !important;
            background: white !important;
          }
          #root > div { display: block !important; min-height: 0 !important; }
          #root > div > .flex-1 { display: block !important; }
          footer, nav, .print-hide { display: none !important; }
          .qr-print-page {
            width: 105mm !important;
            height: 148mm !important;
            min-height: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
            overflow: hidden !important;
            background: white !important;
          }
          .qr-sticker {
            box-sizing: border-box !important;
            width: 105mm !important;
            height: 148mm !important;
            min-height: 148mm !important;
            margin: 0 !important;
            padding: 8mm !important;
            border: 0 !important;
            border-radius: 0 !important;
            box-shadow: none !important;
          }
          .print-hide { display: none !important; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>

      <div className="print-hide mx-auto mb-4 max-w-md rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
        La stampa è già impostata su <strong>A6 verticale</strong> e include una sola etichetta.
        Nella finestra di stampa seleziona carta A6 e scala 100%.
      </div>

      <div className="qr-sticker mx-auto flex flex-col items-center justify-center border bg-white p-8 text-center shadow-sm sm:rounded-2xl">
        <div className="flex w-full items-center justify-center gap-3 border-b border-slate-200 pb-4">
          <img
            src={logoSrc}
            alt="Cryo"
            className="h-12 w-12 object-contain"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
          <div className="min-w-0">
            <div className="text-xl font-bold leading-tight text-slate-900">
              {frigoName}
            </div>
            {azienda ? (
              <div className="mt-1 text-sm text-slate-600">{azienda}</div>
            ) : (
              <div className="mt-1 text-sm text-slate-600">Monitoraggio temperature</div>
            )}
            {reparto ? (
              <div className="text-xs text-slate-500">Reparto: {reparto}</div>
            ) : null}
          </div>
        </div>

        <div className="mt-6 w-[78mm] max-w-full rounded-2xl border-2 border-slate-900 bg-white p-3">
          <img
            src={qrUrl}
            alt="QR inserimento temperatura"
            className="h-auto w-full"
            onLoad={() => setQrReady(true)}
            onError={() => setQrReady(false)}
          />
        </div>

        <div className="mt-5 text-lg font-bold text-slate-900">
          Scansiona per registrare la temperatura
        </div>
        <div className="mt-2 text-xs text-slate-500">
          Frigorifero ID {id}{mac ? ` · ${mac}` : ""}
        </div>

        <button
          className="print-hide mt-6 w-full rounded-xl bg-blue-700 px-4 py-3 font-semibold text-white hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
          onClick={() => window.print()}
          disabled={!qrReady}
        >
          {qrReady ? "Stampa" : "Caricamento QR…"}
        </button>
        <a
          className="print-hide mt-3 w-full box-border rounded-xl border border-blue-700 bg-white px-4 py-3 font-semibold text-blue-700 hover:bg-blue-50"
          href={labelPdfUrl}
          onClick={printThermalLabel}
          target="_blank"
          rel="noopener noreferrer"
        >
          Stampa con etichettatrice
        </a>
        {printerMessage && <div role="status" className="print-hide mt-2 text-sm text-slate-700">{printerMessage}</div>}
        <div className="print-hide mt-2 text-xs text-slate-500">
          PDF 80 × 50 mm · Stampa in dimensioni effettive (100%)
        </div>
      </div>
    </div>
  );
}
